// path: OptionPicker.js
'use client'
import { useEffect, useMemo, useState } from 'react'

function toNum(v) {
  if (v === null || v === undefined) return null
  if (typeof v === 'number') return Number.isFinite(v) ? v : null
  const s = String(v).replace(/[^\d.-]/g, '')
  if (!s) return null
  const n = Number(s)
  return Number.isFinite(n) ? n : null
}

function firstNumber(obj, fields) {
  for (const f of fields) {
    const n = toNum(obj?.[f])
    if (n !== null) return n
  }
  return null
}

function clampInt(n, min, max) {
  const x = Math.floor(Number(n) || 0)
  if (!Number.isFinite(x)) return min
  if (x < min) return min
  if (max != null && x > max) return max
  return x
}

function optionRank(name) {
  const s = String(name || '').toLowerCase()
  // 顏色優先，尺寸第二，其它照原順序
  if (/顏色|颜色|color/.test(s)) return 0
  if (/尺寸|size/.test(s)) return 1
  return 2
}

export default function OptionPicker({
  open,
  product, // 一筆商品資料（含 ProductOption1/2, ProductOption）
  onClose,
  onConfirm, // (skuId, qty, limitedQty, inventoryQty, unitPrice, chosenSku) => void
  cardProduct,
  onSkuChange, // ✅ (chosenSku, unitPrice, oldPrice) => void
  isPreorder = false,
  storeInfo,
}) {
  const opt1Def = product?.ProductOption1 || null // { Name, Data }
  const opt2Def = product?.ProductOption2 || null // { Name, Data }
  const needOpt1 = !!opt1Def && !!product?.IsProductOption1
  const needOpt2 = !!opt2Def && !!product?.IsProductOption2

  const [opt1, setOpt1] = useState('')
  const [opt2, setOpt2] = useState('')
  const [qty, setQty] = useState(1)

  useEffect(() => {
    if (!open) return
    setOpt1(needOpt1 && opt1Def?.Data?.length ? opt1Def.Data[0] : '')
    setOpt2(needOpt2 && opt2Def?.Data?.length ? opt2Def.Data[0] : '')
    setQty(1)
  }, [open, needOpt1, needOpt2, opt1Def, opt2Def])

  // 依使用者選擇，在 ProductOption[] 找到對應 SKU
  const chosenSku = useMemo(() => {
    const skus = product?.ProductOption || []
    if (!skus?.length) return null

    if (needOpt1 && needOpt2) {
      return (
        skus.find((s) => {
          const parts = String(s?.SkuName || '')
            .split('*')
            .map((x) => x.trim())
            .filter(Boolean)

          const p0 = parts[0] ?? ''
          const p1 = parts[1] ?? ''

          return (p0 === opt1 && p1 === opt2) || (p0 === opt2 && p1 === opt1)
        }) || null
      )
    } else if (needOpt1) {
      return skus.find((s) => String(s?.SkuName || '').trim() === opt1) || null
    } else if (needOpt2) {
      return skus.find((s) => String(s?.SkuName || '').trim() === opt2) || null
    }
    return null
  }, [product, needOpt1, needOpt2, opt1, opt2])

  // ✅ 算 SKU 單價/原價（欄位容錯）
  const priceInfo = useMemo(() => {
    const saleFieldsSku = [
      'Price',
      'SalePrice',
      'SellPrice',
      'UnitPrice',
      'FinalPrice',
      'DiscountPrice',
    ]
    const saleFieldsProd = [
      'Price',
      'SalePrice',
      'SellPrice',
      'UnitPrice',
      'FinalPrice',
      'DiscountPrice',
    ]

    const originFieldsSku = [
      'SuggestPrice',
      'OriginPrice',
      'OriginalPrice',
      'ListPrice',
      'MarketPrice',
      'MSRP',
    ]
    const originFieldsProd = [
      'SuggestPrice',
      'OriginPrice',
      'OriginalPrice',
      'ListPrice',
      'MarketPrice',
      'MSRP',
    ]

    const sale =
      firstNumber(chosenSku, saleFieldsSku) ??
      firstNumber(product, saleFieldsProd) ??
      null

    const origin =
      firstNumber(chosenSku, originFieldsSku) ??
      firstNumber(product, originFieldsProd) ??
      null

    const oldPrice =
      origin !== null && sale !== null && origin > sale ? origin : null

    return { unitPrice: sale, oldPrice }
  }, [chosenSku, product])

  // ✅ 一旦 SKU 或價格改變 → 回報給外層
  useEffect(() => {
    if (!open) return
    if (typeof onSkuChange === 'function') {
      onSkuChange(chosenSku, priceInfo.unitPrice, priceInfo.oldPrice)
    }
  }, [open, chosenSku, priceInfo.unitPrice, priceInfo.oldPrice, onSkuChange])

  // ✅ 計算數量上限：min(庫存, 限購)（有提供才套用）
  const maxQty = useMemo(() => {
    const inv = toNum(chosenSku?.InventoryQty)
    const limit = toNum(chosenSku?.LimitedQty)

    let max = null

    // 預購商品不看庫存，只看限購
    if (!isPreorder && inv !== null) max = inv

    if (limit !== null) max = max === null ? limit : Math.min(max, limit)

    if (max === null) return null
    const m = Math.floor(max)
    if (!Number.isFinite(m)) return null
    if (m <= 0) return 0
    return m
  }, [chosenSku?.InventoryQty, chosenSku?.LimitedQty, isPreorder])

  // ✅ 若購物車已存在此 SKU → qty 帶出（並且套用上限）
  useEffect(() => {
    if (!open) return
    if (!cardProduct || cardProduct.length === 0) return

    const skuId = chosenSku?.SkuID ?? null
    if (skuId == null) return

    if (!isPreorder && maxQty !== null && maxQty <= 0) {
      setQty(0)
      return
    }

    const hit = cardProduct.find((c) => Number(c?.SkuID) === Number(skuId))

    if (hit?.Qty) {
      const next = clampInt(hit.Qty, 1, maxQty)
      setQty(next)
    }
  }, [open, cardProduct, chosenSku?.SkuID, maxQty])

  // ✅ SKU / 上限變動時，避免 qty 超過上限
  useEffect(() => {
    if (!open) return

    if (!isPreorder && maxQty !== null && maxQty <= 0) {
      setQty(0)
      return
    }

    setQty((q) => clampInt(q, 1, maxQty))
  }, [open, maxQty])

  const hasStock = isPreorder || maxQty === null || maxQty > 0
  const canSubmit =
    (!needOpt1 || !!opt1) &&
    (!needOpt2 || !!opt2) &&
    qty > 0 &&
    (product?.IsProductOption ? !!chosenSku : true) &&
    hasStock

  // ✅ 產生「顏色/尺寸」顯示順序（顏色優先、尺寸第二）
  const optionItems = useMemo(() => {
    const items = []
    if (needOpt1) {
      items.push({
        key: 'opt1',
        name: opt1Def?.Name || '選項 1',
        data: opt1Def?.Data || [],
        value: opt1,
        setValue: setOpt1,
      })
    }
    if (needOpt2) {
      items.push({
        key: 'opt2',
        name: opt2Def?.Name || '選項 2',
        data: opt2Def?.Data || [],
        value: opt2,
        setValue: setOpt2,
      })
    }

    if (items.length <= 1) return items

    const aRank = optionRank(items[0].name)
    const bRank = optionRank(items[1].name)
    if (aRank === 2 && bRank === 2) return items

    return [...items].sort((a, b) => optionRank(a.name) - optionRank(b.name))
  }, [needOpt1, needOpt2, opt1Def, opt2Def, opt1, opt2])

  const canMinus = hasStock && qty > 1
  const canPlus = hasStock && (maxQty == null ? true : qty < maxQty)

  // ✅ 這組樣式就是「三格連在一起」的 stepper
  const borderColor = '#ced4da'
  const stepperBtnBase = {
    width: 44,
    height: 44,
    border: `1px solid ${borderColor}`,
    background: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 20,
    lineHeight: 1,
    padding: 0,
  }
  const stepperValueStyle = {
    width: 64,
    height: 44,
    borderTop: `1px solid ${borderColor}`,
    borderBottom: `1px solid ${borderColor}`,
    borderLeft: '0',
    borderRight: '0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#fff',
    fontSize: 16,
    userSelect: 'none',
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-[999] bg-black/40 flex items-end md:items-center md:justify-center">
      <div className="relative w-full md:max-w-lg bg-white rounded-t-2xl md:rounded-2xl p-4 md:p-6 shadow-xl">
        {/* ✅ 顏色/尺寸順序調整後，統一用 map 產生 */}
        {optionItems.map((it) => (
          <div className="mb-3" key={it.key}>
            <label className="form-label">{it.name}</label>
            <select
              className="form-select"
              value={it.value}
              onChange={(e) => it.setValue(e.target.value)}
            >
              {it.data.map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
          </div>
        ))}

        {/* ✅ 數量改成「三格連在一起」樣式 */}
        <div className="mb-3 d-flex align-items-center justify-content-between">
          <label className="form-label me-2 mb-0">數量</label>

          <div
            className="d-flex align-items-center"
            style={{ borderRadius: 8, overflow: 'hidden' }}
          >
            <button
              type="button"
              disabled={!canMinus}
              onClick={() => setQty((q) => clampInt(q - 1, 1, maxQty))}
              aria-label="減少數量"
              style={{
                ...stepperBtnBase,
                borderTopLeftRadius: 8,
                borderBottomLeftRadius: 8,
                opacity: canMinus ? 1 : 0.4,
                cursor: canMinus ? 'pointer' : 'not-allowed',
              }}
            >
              −
            </button>

            <div aria-label="目前數量" style={stepperValueStyle}>
              {qty}
            </div>

            <button
              type="button"
              disabled={!canPlus}
              onClick={() => setQty((q) => clampInt(q + 1, 1, maxQty))}
              aria-label="增加數量"
              style={{
                ...stepperBtnBase,
                borderLeft: '0',
                borderTopRightRadius: 8,
                borderBottomRightRadius: 8,
                opacity: canPlus ? 1 : 0.4,
                cursor: canPlus ? 'pointer' : 'not-allowed',
              }}
            >
              +
            </button>
          </div>
        </div>

        {product?.IsProductOption && (
          <div className="mb-2 small text-muted">
            {chosenSku ? (
              <>
                已選：<b>{chosenSku.SkuName}</b>
                {storeInfo?.IsShowInventory && (
                  <>（庫存 {chosenSku.InventoryQty}）</>
                )}
              </>
            ) : (
              <>請選擇完整規格</>
            )}
          </div>
        )}

        <div className="d-grid gap-2">
          <button
            className="btn btn-dark"
            disabled={!canSubmit}
            onClick={() =>
              onConfirm(
                chosenSku?.SkuID ?? null,
                qty,
                chosenSku?.LimitedQty,
                chosenSku?.InventoryQty,
                priceInfo.unitPrice,
                chosenSku
              )
            }
          >
            {!hasStock && !isPreorder ? '補貨中' : '確定加入購物車'}
          </button>
          <button className="btn btn-outline-secondary" onClick={onClose}>
            取消
          </button>
        </div>
      </div>
    </div>
  )
}
